import {
  measurementTemplateListSchema,
  measurementTemplateSchema,
  type AddFieldInput,
  type CreateTemplateInput,
  type MeasurementTemplate,
  type MeasurementUnit,
  type UpdateFieldInput,
  type UpdateTemplateInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * Every measurement-template call the app makes.
 *
 * Note that each mutating call returns the WHOLE template rather than the piece that changed.
 * That is deliberate: field order, keys and grouping are interdependent, and a client that
 * patched its local copy field by field would eventually disagree with the server about the
 * order — which is exactly the bug a tailor notices and nobody can reproduce.
 */

const BASE = '/measurement-templates';

export interface TemplateList {
  templates: MeasurementTemplate[];
  shopDefaultUnit: MeasurementUnit;
}

export async function listTemplates(
  client: ApiClient,
  options: { includeInactive?: boolean } = {},
): Promise<TemplateList> {
  return await client.get(BASE, {
    schema: measurementTemplateListSchema,
    query: options.includeInactive ? { includeInactive: 'true' } : {},
  });
}

export async function getTemplate(
  client: ApiClient,
  garmentTypeKey: string,
): Promise<MeasurementTemplate> {
  return await client.get(`${BASE}/${garmentTypeKey}`, { schema: measurementTemplateSchema });
}

export async function createTemplate(
  client: ApiClient,
  input: CreateTemplateInput,
): Promise<MeasurementTemplate> {
  return await client.post(BASE, input, { schema: measurementTemplateSchema });
}

export async function updateTemplate(
  client: ApiClient,
  garmentTypeKey: string,
  patch: UpdateTemplateInput,
): Promise<MeasurementTemplate> {
  return await client.patch(`${BASE}/${garmentTypeKey}`, patch, {
    schema: measurementTemplateSchema,
  });
}

export async function addField(
  client: ApiClient,
  garmentTypeKey: string,
  input: AddFieldInput,
): Promise<MeasurementTemplate> {
  return await client.post(`${BASE}/${garmentTypeKey}/fields`, input, {
    schema: measurementTemplateSchema,
  });
}

/**
 * Editing a field. The key is in the URL and never in the body — the client could not ask to
 * re-key a field even if it wanted to, which is the same guarantee the API enforces.
 */
export async function updateField(
  client: ApiClient,
  garmentTypeKey: string,
  fieldKey: string,
  patch: UpdateFieldInput,
): Promise<MeasurementTemplate> {
  return await client.patch(`${BASE}/${garmentTypeKey}/fields/${fieldKey}`, patch, {
    schema: measurementTemplateSchema,
  });
}

export async function reorderFields(
  client: ApiClient,
  garmentTypeKey: string,
  order: readonly string[],
): Promise<MeasurementTemplate> {
  return await client.patch(
    `${BASE}/${garmentTypeKey}/fields/order`,
    { order: [...order] },
    { schema: measurementTemplateSchema },
  );
}

export async function deleteField(
  client: ApiClient,
  garmentTypeKey: string,
  fieldKey: string,
): Promise<MeasurementTemplate> {
  return await client.del(`${BASE}/${garmentTypeKey}/fields/${fieldKey}`, undefined, {
    schema: measurementTemplateSchema,
  });
}

/** How many records hold a value for this field, when that is why a delete was refused. */
export function fieldInUseCount(error: unknown): number | null {
  if (!isApiRequestError(error) || error.status !== 409) return null;
  const details = error.details as { reason?: string; usageCount?: number } | undefined;
  if (details?.reason !== 'field_in_use') return null;
  return typeof details.usageCount === 'number' ? details.usageCount : 0;
}
